// Per-role route allow-list. The router itself only role-gates a
// handful of paths (`/lessons`, `/teachers-updates`, etc.) — most
// pages were left open because we never had STUDENT/PARENT roles
// hitting them. Now that they do, we add a single belt-and-braces
// guard inside AppShell that checks the current pathname against
// the allow-list for the caller's role.
//
// Each entry is a PATH PREFIX. `/exams` matches `/exams`,
// `/exams/take`, `/exams/abc123`. Add the longest specific prefix
// you intend to grant — bare `/` would defeat the gate.
//
// Default-deny: every non-overseer role MUST appear with an explicit
// allow-list below. Unknown roles fall through to deny. Only the
// admin tier (CHAIRMAN / ADMIN / DIRECTOR / PRINCIPAL) gets
// default-allow — those roles are gated per-route in the router
// already and we don't want to maintain a parallel allow-list that
// drifts every time a new admin module ships.
//
// CAMP-2026-0109: TEACHER could deep-link to /finance because
// isPathAllowedForRole previously fell through to `return true` for
// any role outside STUDENT/PARENT. Even though the API correctly
// 403s, the UI rendered the empty fee-management shell which reads
// to the tester as a leak. The fix below adds explicit TEACHER +
// STAFF allow-lists and flips the default to deny.

// STUDENT allow-list. `/attendance` is role-aware now: parents and
// students see a per-self analytics view (percentage / 6-month trend /
// calendar) while staff see the mark-attendance UI. The view is
// hard-scoped server-side, so adding the prefix here is safe.
//
// IMPORTANT: must cover every path the role's sidebar / bottom-nav /
// dashboard CTAs link to, or the RoleAccessGate will silently bounce
// the user to /dashboard. Cross-check against sidebar.tsx
// juniorStudentNavGroups + seniorStudentNavGroups every time you
// touch a nav item.
const STUDENT_ALLOWED_PREFIXES: readonly string[] = [
  "/dashboard",
  // CAMP-2026-0970 — their own counselling appointments (family-safe API).
  // NOT the staff `/counselling` page, which stays closed to a pupil.
  "/counselling/my",
  // CAMP-2026-0973 — their own event tickets + QR. NOT the issuer desk.
  "/event-tickets/my",
  // CAMP-2026-0746 — same reasoning as the parent list below: a student can be
  // a meeting participant, and the server already scopes them to their own.
  "/meetings",
  "/profile",
  "/settings/profile",
  "/notifications",
  "/timetable",
  "/attendance",
  "/homework",
  "/playground",
  "/exams",
  "/digital-learner",
  "/newsletter",
  "/library",
  "/curriculum",
  "/learning",
  "/ai-tutor",
  "/adaptive-learning",
  "/scorm",
  "/sel",
  "/forums",
  "/career",
  "/science-lab",
  "/badges",
  "/alumni",
  "/metaverse",
  // NOT /neural (removed 2026-09-30): simulated "focus" readings with
  // generated fatigue advice — no pupil or family may reach it (see
  // lib/preview-gating.ts; the router gates it to CHAIRMAN/CONTROL_CENTER).
  "/communication",
  "/calendar",
  "/feedback",
  // Support (2026-10-01) — raise a request, read our replies. The API
  // (/support) admits every signed-in school role and scopes to what they sent.
  "/support",
  "/certificates",
  // ID cards (2026-09-27) — own / children's cards; the API scopes the list.
  "/id-cards",
  "/hpcard",
  "/donations",
  // NOT /plagiarism (removed 2026-09-30): every /plagiarism/* endpoint is
  // staff-only (CHAIRMAN/ADMIN/DIRECTOR/PRINCIPAL/TEACHER/STAFF) and there is
  // no student-facing one, so the page could only 403 for a pupil.
  "/special-education",
  "/transport",
  "/canteen",
  "/classroom",
  // NOT /studio (removed 2026-09-30): the router gates `/studio` and
  // `/studio/newsletter` to the admin tier and no student nav links there, so
  // the prefix could only ever bounce a pupil (and let no API through).
  "/ptm",
  "/dpdp",
  // Hubba — STUDENT has had a router `hubba` role and a sidebar/bottom-nav
  // entry point (via the More sheet) for a while; it was missing here, so
  // this gate silently bounced every student who reached it back to
  // /dashboard with no explanation.
  "/hubba",
];

// PARENT allow-list. `/attendance` is role-aware: parents see a
// per-child analytics view (percentage / 6-month trend / calendar /
// patterns) hard-scoped to their own children via
// getParentChildIds(). Staff continue to see the mark-attendance UI.
//
// IMPORTANT: must cover every path parentNavGroups in sidebar.tsx
// links to, or the RoleAccessGate will silently bounce the user to
// /dashboard. Cross-check whenever you touch parent nav.
const PARENT_ALLOWED_PREFIXES: readonly string[] = [
  // CAMP-2026-0970 — the child's counselling appointments (family-safe API,
  // scoped to their own children). NOT the staff `/counselling` page.
  "/counselling/my",
  // CAMP-2026-0973 — tickets issued to them / their children, with the QR.
  // NOT the issuer desk at `/event-tickets`.
  "/event-tickets/my",
  // Out-pass requests for their own children + announcing a visit (2026-09-27).
  "/gate-passes",
  "/visitor-passes",
  "/dashboard",
  // CAMP-2026-0746 — a parent invited to a meeting tapped the notification and
  // hit "Not available for your role". The API was already built for them:
  // only POST /meetings (create) is staff-gated, and GET /meetings restricts
  // PARENT and STUDENT to `participants: { some: { userId } }`, so they see
  // the sessions they were invited to and nothing else. The route gate was the
  // only thing in the way.
  "/meetings",

  "/profile",
  "/settings/profile",
  "/notifications",
  "/finance",
  "/exams",
  "/homework",
  "/attendance",
  "/playground",
  "/digital-learner",
  "/newsletter",
  "/timetable",
  "/communication",
  "/ptm",
  "/surveys",
  "/hpcard",
  "/transport",
  "/donations",
  "/learning",
  "/calendar",
  "/feedback",
  // Support (2026-10-01) — see the STUDENT list.
  "/support",
  "/certificates",
  // Badges (2026-09-27) — each child's badges; GET /badges/shelf scopes a
  // PARENT to their own children.
  "/badges",
  // ID cards (2026-09-27) — own / children's cards; the API scopes the list.
  "/id-cards",
  "/library",
  "/forums",
  "/dpdp",
  // My Day — the pre-primary daily experience. Prefix, so it also covers
  // the ?student=/?day= deep link a push notification opens.
  "/my-day",
  // Hubba — same gap as the STUDENT list above: PARENT has had router access
  // for a while but no entry here, so this gate bounced them before they
  // ever reached the page.
  "/hubba",
];

// TEACHER allow-list. Mirrors teacherNavGroups in sidebar.tsx +
// the legitimate deep-link surfaces a teacher operates against:
//   • /students         — "My Students" roster (server scopes to the
//                         teacher's class+sections, no leak)
//   • /admissions       — admissions CRM. TEACHER reads the whole
//                         school pipeline and may RAISE enquiries
//                         (POST /admissions authorises TEACHER — lead
//                         capture while campaigning, CAMP-2026-0181), but
//                         cannot enrol, run Application Review or move
//                         stages (those routes are STAFF + admins only).
//   • /gate-passes       — out-passes for students in sections they
//                         teach (the API scopes it); /studio/gate-passes
//                         stays so an old bookmark reaches its redirect
//   • /visitor-passes    — visitors who came to see them + visits they
//                         announced (API-scoped)
//   • /hr/leave         — own leave/payslip; the bare /hr landing
//                         is intentionally excluded (admin only)
//   • /hr/performance   — CAMP-2026-0463: the reviewed person's OWN
//                         performance reviews. GET /hr/performance
//                         self-scopes a non-admin caller to their own
//                         staff row, and the Performance tab renders a
//                         read-only self view for them (no roster, no
//                         create). Without the prefix the RoleAccessGate
//                         bounced the teacher back to /dashboard, so a
//                         review written about them was unreachable.
// What's explicitly NOT here: /finance, /accounting, /budget,
// /admissions enrol+review writes, /settings (beyond /settings/profile),
// /schools, /qa, /integrations, /udise, /biometric, /compliance,
// /studio/{ids,posters,visitor-passes,event-tickets}, /automation,
// /concession-approvals, /teachers (the staff directory — GET /staff is
// admin-tier only and the owner chose NOT to widen it, 2026-09-30) and
// /communications (the Comms Hub — every WhatsApp/SMS/email/push endpoint it
// calls is admin-tier only). Every absence is intentional —
// adding a path here means a teacher can see it, so check the endpoints the
// page calls first (traps-and-gaps.md §15.1 #11; role-access.test.ts pins it).
const TEACHER_ALLOWED_PREFIXES: readonly string[] = [
  "/dashboard",
  // Brain Arcade. Teachers reach /playground/assign, /playground/assignments/:id
  // and /playground/analytics; the router gates each of those to staff roles,
  // and the API scopes every read to the classes they actually teach. The bare
  // /playground hub is student-only at the router, so granting the prefix here
  // does not hand a teacher the child-facing surface.
  "/playground",
  "/profile",
  "/settings/profile",
  "/notifications",
  "/lessons",
  "/timetable",
  "/attendance",
  "/gradebook",
  "/homework",
  "/exams",
  "/curriculum",
  "/digital-learner",
  "/newsletter",
  "/students",
  "/admissions",
  // `/communication` (messages / notices / announcements) — NOT the
  // admin-only `/communications` Comms Hub, see the list of absences above.
  "/communication",
  "/feedback",
  // Support (2026-10-01) — see the STUDENT list.
  "/support",
  "/calendar",
  "/hr/leave",
  // CAMP-2026-0463 — own performance reviews (self-scoped, read-only).
  "/hr/performance",
  "/library",
  "/classroom",
  "/substitutes",
  "/gate-passes",
  "/visitor-passes",
  "/studio/gate-passes",
  "/hpcard",
  "/cpd",
  "/competency",
  "/badges",
  "/forums",
  "/ptm",
  "/surveys",
  "/lesson-logs",
  "/meetings",
  "/ai-tutor",
  // CAMP-2026-0301 — the teacher dashboard ships an "AI Learning OS" tile and
  // the API already authorises TEACHER on every /learning route
  // (TEACHING_ROLES). Omitting the prefix here made the tile a dead end: the
  // RoleAccessGate bounced straight back to /dashboard with no explanation.
  "/learning",
  // Faircopy — teachers digitise handwritten question papers (desktop-only UI).
  "/faircopy",
  // My Day — only /my-day/class is admitted by the router for teachers, and
  // that page further requires a Nursery teaching assignment. The prefix is
  // needed here because this outer allow-list cannot express an exact path;
  // RequireRole rejects the bare parent route for teachers.
  "/my-day",
  // Hubba — TEACHER was added to the router's `hubba` route roles and to
  // teacherNavGroups; without this the nav entry would render a link this
  // gate then bounces straight back from.
  "/hubba",
  // Certificates (2026-09-26) — the teacher's OWN recognition certificates
  // ("My certificates"). GET /documents/certificates scopes a TEACHER to rows
  // whose staff record is theirs, ISSUED only; issuing stays admin-tier.
  "/certificates",
  // Recognition (2026-09-27) — approval inbox for their own classes +
  // "Nominate a student". GET /recognition/candidates and the nomination
  // route both scope TEACHER via lib/teacher-scope.ts; rule authoring and
  // the insights tiles stay admin-tier (the page hides those tabs for TEACHER).
  "/recognition",
  // ID cards (2026-09-27) — own / children's cards; the API scopes the list.
  "/id-cards",
  // Event tickets (2026-09-27) — the gate scanner only. The page shows a
  // TEACHER nothing but the scanner, and the API admits TEACHER to
  // /documents/tickets/scan and /scan/events and nothing else there.
  "/event-tickets",
  // Donations & Fundraising (2026-09-30) — a teacher raises campaign requests
  // (drafts only; the API's MANAGE_ROLES), posts impact updates on their own
  // campaigns and may give. The module shell narrows the prefix further
  // (lib/donations/sections.ts): campaigns, impact, give and checkout only.
  "/donations",
];

// STAFF allow-list. Front-desk / non-teaching support staff. Mirrors
// staffNavGroups in sidebar.tsx, plus /finance (the API explicitly
// authorizes STAFF for fee collection — collection windows are a
// front-desk task) and the security desk (/gate-passes, /visitor-passes;
// /studio/visitor-passes only so an old bookmark reaches its redirect).
// Nothing else.
//
// CAMP-2026-0139: front-desk staff run the admissions CRM (enquiry
// intake → enrolment) and collect fees, so /admissions is granted
// here to match the staffNavGroups Admissions + Finance entries and
// the STAFF authorize() grants on the admissions CRM routes. The API
// keeps DELETE /admissions/:id and the API-key / template-config
// routes admin-only, so the write surface stays scoped even though
// the page is reachable.
const STAFF_ALLOWED_PREFIXES: readonly string[] = [
  "/dashboard",
  "/profile",
  "/settings/profile",
  "/notifications",
  "/hr/leave",
  // CAMP-2026-0463 — own performance reviews (self-scoped, read-only).
  "/hr/performance",
  // NOT `/communications` (the admin-only Comms Hub) — see TEACHER above.
  "/communication",
  // CAMP-2026-1011 — video meetings. STAFF is already a meeting host and
  // participant in the API (MEETING_HOST_ROLES / MEETING_PARTICIPANT_ROLES), and
  // GET /meetings scopes a non-admin to meetings they host or were invited to,
  // so the missing prefix was the only thing keeping staff out. The page shows
  // the "schedule" form only to creator roles and the admin-only controls
  // (end/cancel any meeting, guest link of others') stay admin-tier.
  "/meetings",
  "/feedback",
  // Support (2026-10-01) — see the STUDENT list.
  "/support",
  "/calendar",
  "/library",
  "/finance",
  "/admissions",
  // Security desk (2026-09-27): scan passes, visitors, mark exits. The old
  // Studio path stays so a bookmark reaches its redirect.
  "/gate-passes",
  "/visitor-passes",
  "/studio/visitor-passes",
  "/digital-learner",
  "/newsletter",
  // Health & Safety (clinic/medical records) — authorize() on
  // GET/POST /health/* includes STAFF (front-desk logs incidents,
  // dispenses medication). This was missing here even though the API
  // allowed it, so the RoleAccessGate bounced STAFF before they ever
  // reached the page.
  "/health",
  // Faircopy — front-desk digitises handwritten receipts / notices.
  "/faircopy",
  // Hubba — STAFF was added to the router's `hubba` route roles and to
  // staffNavGroups.
  "/hubba",
  // Certificates (2026-09-26) — own recognition certificates, read-only; the
  // API scopes STAFF to their own ISSUED rows.
  "/certificates",
  // Badges (2026-09-27) — own badges (Service Star, Mentor…), read-only.
  "/badges",
  // ID cards (2026-09-27) — own / children's cards; the API scopes the list.
  "/id-cards",
  // Event tickets (2026-09-27) — the gate scanner only (same as TEACHER).
  "/event-tickets",
  // Donations & Fundraising (2026-09-30) — the fundraising / finance desk:
  // offline entry, verification, receipts, reconciliation, donor CRM,
  // pledges, communications (the API's OPS_ROLES). Settings stay admin-tier;
  // the module shell keeps STAFF off /donations/settings.
  "/donations",
];

// DRIVER allow-list. The entire driver app shell lives at /drive — no
// other module is reachable. We DO grant /dashboard so that the
// transient router state during RoleAwareHomeRedirect (which sends
// DRIVER → /drive on login) doesn't dead-lock against this gate.
// /profile + /settings/profile + /notifications follow the standard
// minimum for any logged-in role.
const DRIVER_ALLOWED_PREFIXES: readonly string[] = [
  "/dashboard",
  "/drive",
  "/profile",
  "/settings/profile",
  "/notifications",
  // Hubba — DRIVER was added to the router's `hubba` route roles and gets a
  // "My Route" starter prompt; the sidebar/More-sheet entry needs this or
  // this gate bounces it straight back.
  "/hubba",
];

// KIOSK — entrance tablet account. Only the attendance kiosk surface.
// /dashboard granted so the post-login RoleAwareHomeRedirect doesn't
// dead-lock before the redirect fires.
const KIOSK_ALLOWED_PREFIXES: readonly string[] = [
  "/dashboard",
  "/attendance/kiosk",
  "/attendance", // needed for the attendance API calls from inside the kiosk
];

// OVERSEER-ONLY paths. The admin tier default-allows below (see the header
// comment), which is right for the ~90 modules an ADMIN and a CHAIRMAN both
// operate — but it is wrong for a surface whose API is restricted to the two
// multi-school roles. This short list is checked BEFORE that default-allow so
// ADMIN and PRINCIPAL are denied here rather than at the router, and so
// `canRoleOpenHref` stops the nav from rendering a link that would bounce.
//
// `/school-health` is the picked school's rating. Its API,
// GET /dashboard/network/schools/:id/rating, is an `overseerHubRoute`: it
// 403s every role outside CHAIRMAN / DIRECTOR and resolves the school against
// the caller's UserSchool GRANTS, which an ADMIN or PRINCIPAL does not carry
// (their school comes from the JWT). An admin's own school health is a real
// idea, but it needs an endpoint scoped the way an admin is scoped — not this
// one — so the page stays overseer-only until that exists rather than
// shipping a menu entry that can only 403.
//
// NOTE: `/schools` and `/qa` are also overseer/chairman-only, and are NOT
// listed here — they are gated by RequireRole in the router today and moving
// them would change what ADMIN sees on a deep link (a redirect becomes a
// notice). Left alone deliberately.
const OVERSEER_ONLY_PREFIXES: readonly string[] = [
  "/school-health",
];

// PLATFORM-ONLY paths (2026-10-01). `/tickets` is the internal QA board: the
// platform team (CONTROL_CENTER, and CHAIRMAN who works it) triages customers'
// requests there. A school's people raise requests at `/support`, which never
// links here, so every other role is denied the path — including the admin tier
// that otherwise default-allows — and `canRoleOpenHref` stops a stale link
// (an old TICKET notification) from rendering for them. The router carries the
// same list (`TICKETS_BOARD_ROLES`); role-access.test.ts pins the two together.
const PLATFORM_ONLY_PREFIXES: readonly string[] = [
  "/tickets",
];

/**
 * Who may raise / follow QA tickets on `/tickets` in a QA environment
 * (staging, local, preview — `isQaEnv()`). Owner, 2026-10-06: on staging every
 * tester signs in as the role they are testing, so the "Raise Ticket" pill and
 * the board must reach every desk role, not only the platform team. Production
 * is unchanged — `/tickets` stays CONTROL_CENTER + CHAIRMAN there
 * (the router's `TICKETS_BOARD_ROLES`). DRIVER and KIOSK are device shells with
 * no top bar. The API already lets any signed-in user file a ticket and see their
 * own (routes/tickets: triage stays with TICKET_TRIAGE_ROLES).
 */
export const QA_TICKET_ROLES: readonly string[] = [
  "CONTROL_CENTER", "CHAIRMAN", "DIRECTOR", "ADMIN", "PRINCIPAL",
  "TEACHER", "STAFF", "PARENT", "STUDENT",
];

/**
 * Who may read `GET /transport/routes` — the API's authorize list
 * (apps/api/src/routes/transport/index.ts): the admin tier plus STAFF. A screen
 * other roles can open (Admissions is open to TEACHER) must pass this as the
 * query's `enabled`, or every visit fires a request that can only come back 403.
 */
export const TRANSPORT_ROUTES_READ_ROLES: readonly string[] = ["CHAIRMAN", "ADMIN", "DIRECTOR", "PRINCIPAL", "STAFF"];

export function canReadTransportRoutes(role: string | null | undefined): boolean {
  return !!role && TRANSPORT_ROUTES_READ_ROLES.includes(role);
}

// WP-7c — sub-pages of /exams that are NOT for every role that holds the `/exams` prefix. A parent or a
// pupil reaches /exams (their results, the re-evaluation request on a published paper) and a teacher
// reaches it too, so the prefix alone would let them open the office's desks and see "an empty page that
// 403s". The router carries the same role lists with `RequireRole`; role-access.test.ts pins the two
// (and the API's authorize() lists, via the fixture) together.
//   office desks      review queue, re-evaluation requests, promotion  -> the admin tier
//   graders' desk     co-scholastic grades                             -> the admin tier + TEACHER
const EXAMS_OFFICE_ONLY_PREFIXES: readonly string[] = ["/exams/review-queue", "/exams/re-evaluation", "/exams/promotion"];
const EXAMS_GRADER_ONLY_PREFIXES: readonly string[] = ["/exams/co-scholastic"];

/** True when `role` holds `/exams` but not this particular /exams sub-page. */
function examsSubpageDenied(role: string, pathname: string): boolean {
  if (matchesAnyPrefix(pathname, EXAMS_OFFICE_ONLY_PREFIXES)) return true;
  if (matchesAnyPrefix(pathname, EXAMS_GRADER_ONLY_PREFIXES)) return role !== "TEACHER";
  return false;
}

/** The explicit allow-lists, by role, read-only. Exported so
 *  `role-access.test.ts` can check every prefix against the API gate fixture
 *  (`role-access-api-gates.fixture.ts`); nothing in the app should read it —
 *  ask `isPathAllowedForRole` / `canRoleOpenHref` instead. */
export const ROLE_ALLOWED_PREFIXES: Readonly<Record<"STUDENT" | "PARENT" | "TEACHER" | "STAFF" | "DRIVER" | "KIOSK", readonly string[]>> = {
  STUDENT: STUDENT_ALLOWED_PREFIXES,
  PARENT: PARENT_ALLOWED_PREFIXES,
  TEACHER: TEACHER_ALLOWED_PREFIXES,
  STAFF: STAFF_ALLOWED_PREFIXES,
  DRIVER: DRIVER_ALLOWED_PREFIXES,
  KIOSK: KIOSK_ALLOWED_PREFIXES,
};

/** Returns true if `pathname` is allowed for the given role.
 *  Admin tiers (CHAIRMAN/ADMIN/DIRECTOR/PRINCIPAL) default-allow —
 *  per-route RequireRole guards in the router itself enforce the
 *  CHAIRMAN-only / overseer-only paths. Everyone else (TEACHER,
 *  STAFF, STUDENT, PARENT) must match an explicit allow-list. An
 *  unknown role defaults to deny. */
export function isPathAllowedForRole(role: string | undefined, pathname: string): boolean {
  // Auth still hydrating — allow the navigation so we don't bounce
  // mid-restore. The AppShell only mounts after isAuthenticated,
  // so this branch only catches the brief race before the user
  // object is populated.
  if (!role) return true;

  // CAMP-2026-0358 — the bare root "/" is NEVER a real page. It is the
  // layout index route, whose only job is to render RoleAwareHomeRedirect
  // and immediately <Navigate> the user to their role's home (/dashboard,
  // /drive, /attendance/kiosk). That <Navigate> is a CHILD of the
  // RoleAccessGate, so if "/" is treated as "not allowed" the gate renders
  // the denial screen INSTEAD of the redirect — the bounce never fires and
  // the user is trapped on "Not available for your role" until they tap
  // "Back to dashboard" by hand. This bit every allow-listed role
  // (PARENT / STUDENT / TEACHER / STAFF) on cold start, since "/" is in no
  // allow-list; admin tiers escaped only because they default-allow. Let the
  // redirect source through unconditionally so the home redirect can run —
  // whatever it redirects TO is then gated normally. Belt-and-braces for the
  // whole class: no allow-list can ever trap the index redirect again.
  const isRedirectRoot = pathname.replace(/\/+$/, "") === "";
  if (isRedirectRoot) return true;

  // QA environments: every desk role raises tickets on the board (see QA_TICKET_ROLES).
  if (isQaEnv() && matchesAnyPrefix(pathname, PLATFORM_ONLY_PREFIXES)) return QA_TICKET_ROLES.includes(role);

  switch (role) {
    case "STUDENT":   return !examsSubpageDenied(role, pathname) && matchesAnyPrefix(pathname, STUDENT_ALLOWED_PREFIXES);
    case "PARENT":    return !examsSubpageDenied(role, pathname) && matchesAnyPrefix(pathname, PARENT_ALLOWED_PREFIXES);
    case "TEACHER":   return !examsSubpageDenied(role, pathname) && matchesAnyPrefix(pathname, TEACHER_ALLOWED_PREFIXES);
    case "STAFF":     return !examsSubpageDenied(role, pathname) && matchesAnyPrefix(pathname, STAFF_ALLOWED_PREFIXES);
    case "DRIVER":    return matchesAnyPrefix(pathname, DRIVER_ALLOWED_PREFIXES);
    case "KIOSK":     return matchesAnyPrefix(pathname, KIOSK_ALLOWED_PREFIXES);
    case "CHAIRMAN":
      return true;
    case "DIRECTOR":
      return !matchesAnyPrefix(pathname, PLATFORM_ONLY_PREFIXES);
    case "ADMIN":
    case "PRINCIPAL":
      // Default-allow, minus the multi-school surfaces and the platform board above.
      return !matchesAnyPrefix(pathname, OVERSEER_ONLY_PREFIXES) && !matchesAnyPrefix(pathname, PLATFORM_ONLY_PREFIXES);
    default:
      // Unknown role token — log to console for ops visibility but
      // deny everything except /dashboard so RoleAccessGate's
      // Navigate-to-/dashboard fallback doesn't infinite-loop on a
      // page that's itself denied. Production should never hit this;
      // the union is closed in the Prisma schema. Keeps the page
      // from rendering for any future role token we haven't reasoned
      // about yet.
      if (typeof console !== "undefined") {
        console.warn("[role-access] unknown role denied:", role);
      }
      return matchesAnyPrefix(pathname, ["/dashboard"]);
  }
}

/**
 * CAMP-2026-1031 — the bare `/settings` is the school-settings console: it is in
 * no allow-list but the admin tier's default-allow, so a PARENT / STAFF / TEACHER /
 * STUDENT who clicked "Settings" in the user menu hit "Not available for your
 * role". The Settings page itself already shows such a role only its personal
 * tabs, and those live under `/settings/profile` (which every role holds). We
 * deliberately do NOT add a `/settings` prefix to the allow-lists: it would also
 * open `/settings/classes-setup` and `/settings/dashboard-builder` (open routes in
 * the router, admin-only in the API) to those roles. Instead the gate sends the
 * bare `/settings` of a non-admin role to its own settings home.
 * Returns the path to redirect to, or null when no redirect applies.
 */
export function settingsRedirectFor(role: string | undefined, pathname: string): string | null {
  if (!role) return null;
  if (pathname.replace(/\/+$/, "") !== "/settings") return null;
  if (role === "PARENT" || role === "STUDENT" || role === "TEACHER" || role === "STAFF" || role === "DRIVER") {
    return "/settings/profile";
  }
  return null;
}

/** True when a link to `href` (path, optionally with ?query / #hash) would
 *  actually open for `role` rather than landing on the "Not available for
 *  your role" gate. Use it to decide whether to render a navigating control
 *  at all. Unlike `isPathAllowedForRole`, an unknown/absent role is NOT
 *  optimistically allowed — a link that may dead-end is worse than no link. */
export function canRoleOpenHref(role: string | null | undefined, href: string | null | undefined): boolean {
  if (!role || !href) return false;
  const path = href.split(/[?#]/)[0] ?? "";
  if (!path.startsWith("/")) return false;
  return isPathAllowedForRole(role, path);
}

function matchesAnyPrefix(pathname: string, prefixes: readonly string[]): boolean {
  // Strip any trailing slash so `/exams/` and `/exams` both match.
  const clean = pathname.replace(/\/+$/, "") || "/";
  for (const p of prefixes) {
    if (clean === p) return true;
    if (clean.startsWith(p + "/")) return true;
  }
  return false;
}import { isQaEnv } from "./qa-env";

